// 自研灯箱: 精确居中 + 手势缩放 + 箭头自动隐藏 + info 照片信息
import { useRef, useState, useCallback, useEffect } from 'react';
import type { FileItem } from '../lib/db';
import { isVideo, isImage, formatBytes, formatDuration, formatDate } from '../lib/utils';
import { makeT, type Lang } from '../lib/i18n';

export function VaultLightbox({
  open, index, items, lang, onClose, onDelete, onShowDetail,
}: {
  open: boolean;
  index: number;
  items: FileItem[];
  lang: Lang;
  onClose: () => void;
  onDelete: (item: FileItem) => void;
  onShowDetail: (item: FileItem) => void;
}) {
  const t = makeT(lang);
  const [idx, setIdx] = useState(index);
  const [playing, setPlaying] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [scale, setScale] = useState(1);
  const vidRef = useRef<HTMLVideoElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const pinchDist = useRef(0);
  const pinchScale = useRef(1);
  const hideTimer = useRef<number | null>(null);
  const lastTap = useRef(0);

  // 打开或 index 变化时, 同步 idx (否则 useState(index) 只在首次, 点击别的tile不更新)
  useEffect(() => {
    if (open) { setIdx(index); setScale(1); setShowInfo(false); }
  }, [open, index]);

  const item = open ? items[idx] : undefined;
  const isVid = item?.mime?.startsWith('video/');

  const next = useCallback(() => { setIdx((i) => Math.min(items.length - 1, i + 1)); setScale(1); setShowInfo(false); showNavTemporarily(); }, [items.length]);
  const prev = useCallback(() => { setIdx((i) => Math.max(0, i - 1)); setScale(1); setShowInfo(false); showNavTemporarily(); }, []);

  const showNavTemporarily = () => {
    setShowNav(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setShowNav(false), 1500);
  };

  // 打开时显示箭头, 1.5s 后隐藏 (暂停滑动)
  useEffect(() => {
    if (!open) return;
    setShowNav(true);
    showNavTemporarily();
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [open]);

  const toggleVideo = () => {
    const v = vidRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      pinchDist.current = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchScale.current = scale;
    }
    showNavTemporarily();
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (pinchDist.current > 0) {
        const newScale = Math.max(1, Math.min(6, pinchScale.current * (d / pinchDist.current)));
        setScale(newScale);
      }
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // 单击隐藏箭头 (小幅度且快速)
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      const now = Date.now();
      if (now - lastTap.current < 300) { /* double tap: reset scale */ setScale(1); }
      else { setShowNav(false); }
      lastTap.current = now;
    } else if (scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next(); else prev();
    }
    pinchDist.current = 0;
  };
  const onWheel = (e: React.WheelEvent) => {
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setScale((s) => Math.max(1, Math.min(6, s + delta)));
    showNavTemporarily();
  };

  if (!open || !item) return null;

  const url = '/api/files/' + item.uuid + '/raw';
  const mediaStyle = { transform: 'scale(' + scale + ')', transition: 'transform .15s' };
  const rows: { k: string; v: string }[] = [];
  if (item.width && item.height) rows.push({ k: t('detail.res'), v: item.width + ' × ' + item.height });
  if (item.takenAt) rows.push({ k: t('detail.takenAt'), v: formatDate(item.takenAt, lang) });
  if (isVid && item.duration) rows.push({ k: t('detail.duration'), v: formatDuration(item.duration) });
  rows.push({ k: t('detail.format'), v: item.mime || '-' });
  rows.push({ k: t('detail.size'), v: formatBytes(item.size) });

  return (
    <div className="lb-viewer" onClick={onClose}>
      <div className="lb-toolbar" onClick={(e) => e.stopPropagation()}>
        <span className="lb-counter">{idx + 1} / {items.length}</span>
        <div className="lb-actions">
          <a href={'/api/files/' + item.uuid + '/download'} download={item.originalName || item.displayName}>⬇</a>
          <button onClick={() => setShowInfo((s) => !s)}>ℹ️</button>
          <button onClick={() => onDelete(item)} style={{ color: 'var(--danger)' }}>🗑</button>
          <button onClick={onClose}>✕</button>
        </div>
      </div>

      <div className="lb-body" onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onWheel={onWheel}>
        {isImage(item.mime) && <img src={url} alt={item.displayName} className="lb-media" style={mediaStyle} />}
        {isVid && <video ref={vidRef} src={url} controls playsInline autoPlay={false} className="lb-media" />}
      </div>

      {/* 左右切换箭头: 操作时显示, 暂停/单击后隐藏 */}
      {showNav && idx > 0 && <button className="lb-nav lb-prev" onClick={(e) => { e.stopPropagation(); prev(); }}>‹</button>}
      {showNav && idx < items.length - 1 && <button className="lb-nav lb-next" onClick={(e) => { e.stopPropagation(); next(); }}>›</button>}

      {/* info 面板: 显示当前照片信息 */}
      {showInfo && (
        <div className="lb-info-panel" onClick={(e) => e.stopPropagation()}>
          <div className="lb-info-title">{item.displayName}</div>
          {rows.map((r) => <div className="row" key={r.k}><span className="k">{r.k}</span><span className="v">{r.v}</span></div>)}
        </div>
      )}

      <div className="lb-info" onClick={(e) => e.stopPropagation()}>
        {item.displayName} · {formatBytes(item.size)}
      </div>
    </div>
  );
}
